// The comparison page's facts (pages/Compare.tsx): what popular website
// chatbots charge, each from its own public pricing page.
//
// This page is the one place we print a competitor's price, and the reason
// marketing.ts does not still holds: prices change, and a stale number about
// a rival is wrong and actionable. So every figure here was read from the
// linked page on CHECKED_ON, the page says so, and the daily admin digest
// (functions/lib/adminReport.js, COMPARISON_CHECKED_ON, kept equal by a test)
// says when they are more than 60 days old. Re-check them all, then move the
// date in both places.
//
// Each product counts usage in its own unit (messages, credits, queries,
// conversations, outcomes), so each row says which, and no row converts one
// into another.

import { EARLY_ACCESS, FREE_AGENT_LIMIT, FREE_ANSWERS_PER_MONTH, PLANS, answers, earlyAccessActive, usd } from "./plans";

export const CHECKED_ON = "2026-10-20";

export type Rival = {
  name: string;
  pricingUrl: string;
  free: string;
  entry: string;
  includes: string;
  extras: string;
};

export const OURS: Rival = {
  name: "Build Bots Now",
  pricingUrl: "https://buildbotsnow.com/#pricing",
  free: `Yes: ${FREE_ANSWERS_PER_MONTH} answers a month, ${FREE_AGENT_LIMIT === 1 ? "1 bot" : `up to ${FREE_AGENT_LIMIT} bots`}, no card`,
  entry: earlyAccessActive()
    ? `${usd(PLANS.starter.priceUsd)} a month per bot (Starter, early access; ${usd(EARLY_ACCESS.laterPriceUsd)} from ${EARLY_ACCESS.untilLabel})`
    : `${usd(PLANS.starter.priceUsd)} a month per bot (Starter)`,
  includes: `${answers(PLANS.starter.includedAnswers)} answers a month`,
  extras: `Extra answers ${usd(PLANS.starter.overagePer1000Usd)} per 1,000, up to a limit you set. Hiding our name is free on any paid plan.`
};

export const RIVALS: Rival[] = [
  {
    name: "Chatbase",
    pricingUrl: "https://www.chatbase.co/pricing",
    free: "No free plan; a 7-day trial",
    entry: "$40 a month (Hobby), or $32 billed yearly",
    includes: "700 message credits a month, 1 agent",
    extras: "Extra credits $40 per 1,000. Removing their branding $1,188 a year."
  },
  {
    name: "SiteGPT",
    pricingUrl: "https://sitegpt.ai/pricing",
    free: "No free plan; a 7-day trial, card required",
    entry: "$59 a month (Starter), or $39 billed yearly",
    includes: "Up to 4,000 messages a month, 1 chatbot",
    extras: "Extra 5,000 messages $39 a month. Removing their branding $39 a month."
  },
  {
    name: "DocsBot",
    pricingUrl: "https://docsbot.ai/pricing",
    free: "Yes: 100 AI credits a month, 1 bot",
    entry: "$49 a month (Personal)",
    includes: "5,000 AI credits a month, 1 bot",
    extras: "Next plan $149 a month for 15,000 credits and 3 bots."
  },
  {
    name: "CustomGPT.ai",
    pricingUrl: "https://customgpt.ai/pricing/",
    free: "No free plan; a 7-day trial, card required",
    entry: "$99 a month (Standard), or $89 billed yearly",
    includes: "1,000 queries a month, 10 agents",
    extras: "Next plan $499 a month for 5,000 queries."
  },
  {
    name: "Tidio (Lyro AI)",
    pricingUrl: "https://www.tidio.com/pricing/",
    free: "50 Lyro conversations, once",
    entry: "Lyro from $32.50 a month",
    includes: "50 AI conversations a month",
    extras: "Larger tiers up to 1,000 conversations a month. Tidio's own plans are priced separately."
  },
  {
    name: "Intercom (Fin)",
    pricingUrl: "https://www.intercom.com/pricing",
    free: "No free plan",
    entry: "$0.99 per outcome",
    includes: "Pay per question it resolves or hands off",
    extras: "Intercom seats from $29 each a month, or Fin alone with a monthly minimum (for example 50 outcomes)."
  }
];

// Where we are different, said as facts about us.
export const DIFFERENCES: { title: string; body: string }[] = [
  {
    title: "A free plan that answers real customers",
    body: `A bot that answers ${FREE_ANSWERS_PER_MONTH} questions a month, live on your site, for as long as you like. No trial clock and no card.`
  },
  {
    title: "Priced per bot, from $10",
    body: `Each bot has its own plan, so a second bot for another product or client costs ${usd(PLANS.starter.priceUsd)} a month, not a jump to a bigger tier. Your team is included at no extra cost.`
  },
  {
    title: "No website needed",
    body: "Every bot has its own page at a short link and a QR code, for business cards, menus, shop windows and your email signature."
  },
  {
    title: "Hands off to a person",
    body: "When a visitor needs a human, it passes them to you by email on every plan, and on paid plans in Slack, Discord, a webhook or live chat. Paid plans also book meetings through Cal.com and Calendly."
  },
  {
    title: "No surprise bills",
    body: "Extra answers are billed per 1,000 up to a monthly limit you set, and you can cap how many questions one visitor asks."
  }
];

// Said plainly, because a comparison that admits nothing is not believed.
export const WHEN_THEY_FIT = [
  "If you want a full customer-service suite, with a shared inbox for your team, tickets and many channels in one place, Intercom and Tidio are built for that. We focus on the assistant and hand the hard cases to the tools you already use.",
  "If you need many bots under one bill or enterprise contracts, plans like CustomGPT.ai's bundle more agents together."
];

export function checkedOnLabel(iso = CHECKED_ON): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}
